import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, Globe, TrendingUp, Link2, Users, BarChart3,
  ArrowUpRight, ArrowDownRight, ExternalLink, Plus, Loader2,
  Globe2, Activity, Target, CheckCircle, AlertCircle, Settings,
  Clock, Trash2, Code2, Cpu, Megaphone, Swords, Shield, CreditCard,
  PieChart as PieChartIcon, Zap, Eye
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import { competitorService } from '../../services/competitorService';
import toast from 'react-hot-toast';

interface TechnologyStack {
  cms: string[];
  ecommerce: string[];
  frameworks: string[];
  analytics: string[];
  cdn: string[];
  hosting: string[];
  marketing: string[];
  payments: string[];
  security: string[];
  ui: string[];
  media: string[];
  totalCount: number;
}

interface AdsIntelligence {
  hasGoogleAds: boolean;
  hasMetaAds: boolean | null;
  googleAdsCount: number;
}

interface Competitor {
  domain: string;
  keywordOverlap: number;
  overlapPercentage: number;
  commonKeywords: string[];
  avgPosition: number | null;
  competitionLevel: 'high' | 'medium' | 'low';
}

interface SiteData {
  domain: string;
  organicTraffic: number;
  paidTraffic: number;
  domainAuthority: number;
  backlinks: number;
  referringDomains: number;
  organicKeywords: number;
  trafficValue: number;
  trafficHistory: { date: string; organic: number; paid: number }[];
  topPages: { url: string; traffic: number; keywords: number; share: number }[];
  topKeywords: { keyword: string; position: number; volume: number; traffic: number; source?: string }[];
  trafficByCountry: { country: string; traffic: number; share: number }[];
  technologyStack?: TechnologyStack | null;
  adsIntelligence?: AdsIntelligence | null;
  competitors?: Competitor[];
  competitionMetrics?: {
    competitionLevel: string;
    topCompetitor: string;
    topCompetitorOverlap: number;
  } | null;
  analysisConfidence?: string;
  verifiedKeywordsCount?: number;
}

interface ExplorationHistoryItem {
  id: string;
  domain: string;
  organicTraffic: number;
  domainAuthority: number;
  backlinks: number;
  organicKeywords: number;
  createdAt: string;
}

type TabType = 'overview' | 'keywords' | 'technologies' | 'competitors' | 'ads';

const SiteExplorerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [siteData, setSiteData] = useState<SiteData | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Exploration history state
  const [history, setHistory] = useState<ExplorationHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const COUNTRY_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  const TECH_CATEGORY_COLORS: Record<string, string> = {
    cms: '#3B82F6',
    ecommerce: '#10B981',
    frameworks: '#F59E0B',
    analytics: '#EF4444',
    cdn: '#8B5CF6',
    hosting: '#EC4899',
    marketing: '#14B8A6',
    payments: '#F97316',
    security: '#6366F1',
    ui: '#84CC16',
    media: '#06B6D4'
  };

  // Load exploration history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await competitorService.getExplorationHistory({ limit: 10 });
      if (response.success) {
        setHistory(response.data || []);
      }
    } catch (error) {
      console.error('Error loading exploration history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDeleteHistory = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await competitorService.deleteExploration(id);
      setHistory(prev => prev.filter(h => h.id !== id));
      toast.success(t('common.deleted', 'Deleted'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleLoadFromHistory = async (item: ExplorationHistoryItem) => {
    setDomain(item.domain);
    setIsLoading(true);
    try {
      const response = await competitorService.getExplorationById(item.id);
      if (response.success && response.data) {
        setSiteData(response.data);
        setActiveTab('overview');
      }
    } catch (error) {
      // Fallback: do a fresh exploration
      try {
        const response = await competitorService.exploreSite(item.domain);
        if (response.success && response.data) {
          setSiteData(response.data);
          setActiveTab('overview');
        }
      } catch (err) {
        toast.error(t('errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!domain.trim()) return;

    setIsLoading(true);
    setActiveTab('overview');
    try {
      const response = await competitorService.exploreSite(domain.trim());
      if (response.success && response.data) {
        setSiteData(response.data);
        // Refresh history after new exploration
        loadHistory();
      } else {
        toast.error(t('competitors.noDataFound', 'No data found for this domain. Try adding it as a competitor and analyzing.'));
        setSiteData(null);
      }
    } catch (error) {
      console.error('Error exploring site:', error);
      toast.error(t('errors.generic'));
      setSiteData(null);
    } finally {
      setIsLoading(false);
    }
  }, [domain, t]);

  const handleAddCompetitor = async () => {
    if (!siteData) return;
    try {
      const response = await competitorService.createCompetitor({
        name: siteData.domain,
        domain: siteData.domain
      });

      // Redirect to detail page and auto-start analysis
      if (response.success && response.data?.id) {
        toast.success(t('competitors.addSuccessAnalyzing', 'Concorrente adicionado! Iniciando análise...'));
        navigate(`/competitors/${response.data.id}?analyze=true`);
      } else {
        toast.success(t('competitors.addSuccess', 'Competitor added successfully'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCurrency = (num: number | undefined | null) => {
    if (num === undefined || num === null) return 'R$ -';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(num);
  };

  const getTechCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactNode> = {
      cms: <Globe2 className="w-4 h-4" />,
      ecommerce: <CreditCard className="w-4 h-4" />,
      frameworks: <Code2 className="w-4 h-4" />,
      analytics: <BarChart3 className="w-4 h-4" />,
      cdn: <Zap className="w-4 h-4" />,
      hosting: <Cpu className="w-4 h-4" />,
      marketing: <Megaphone className="w-4 h-4" />,
      payments: <CreditCard className="w-4 h-4" />,
      security: <Shield className="w-4 h-4" />,
      ui: <PieChartIcon className="w-4 h-4" />,
      media: <Eye className="w-4 h-4" />
    };
    return icons[category] || <Settings className="w-4 h-4" />;
  };

  const getTechCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      cms: 'CMS',
      ecommerce: 'E-commerce',
      frameworks: 'Frameworks',
      analytics: 'Analytics',
      cdn: 'CDN',
      hosting: 'Hosting',
      marketing: 'Marketing',
      payments: 'Payments',
      security: 'Security',
      ui: 'UI Libraries',
      media: 'Media'
    };
    return labels[category] || category;
  };

  const tabs = [
    { id: 'overview' as TabType, label: t('common.overview', 'Overview'), icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'keywords' as TabType, label: t('keywords.keywords', 'Keywords'), icon: <Target className="w-4 h-4" /> },
    { id: 'technologies' as TabType, label: t('competitors.technologies', 'Technologies'), icon: <Code2 className="w-4 h-4" /> },
    { id: 'competitors' as TabType, label: t('competitors.competitors', 'Competitors'), icon: <Swords className="w-4 h-4" /> },
    { id: 'ads' as TabType, label: t('competitors.adsIntelligence', 'Ads'), icon: <Megaphone className="w-4 h-4" /> }
  ];

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {t('nav.sections.siteExplorer', 'Explorar site')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          {t('competitors.siteExplorerDescription', 'Analyze any domain and discover traffic metrics, keywords, technologies and competitors')}
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <Input
                placeholder={t('competitors.enterDomain', 'Enter a domain (e.g.: example.com)')}
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                leftIcon={<Globe className="w-5 h-5" />}
              />
            </div>
            <Button type="submit" disabled={isLoading || !domain.trim()} className="whitespace-nowrap flex-shrink-0">
              {isLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {t('common.analyze', 'Analyze')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Recent Explorations History */}
      {!siteData && !isLoading && history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {t('competitors.recentExplorations', 'Recent Explorations')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleLoadFromHistory(item)}
                  className="group p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Globe2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {item.domain}
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleDeleteHistory(item.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                      title={t('common.delete', 'Delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <div>
                      <span className="block text-gray-400">DA</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{item.domainAuthority || '-'}</span>
                    </div>
                    <div>
                      <span className="block text-gray-400">{t('competitors.traffic', 'Traffic')}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {item.organicTraffic ? formatNumber(item.organicTraffic) : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="block text-gray-400">{t('competitors.backlinks', 'Backlinks')}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {item.backlinks ? formatNumber(item.backlinks) : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="block text-gray-400">{t('keywords.keywords', 'Keywords')}</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {item.organicKeywords || '-'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('competitors.analyzingDomain', 'Analyzing domain...')}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {t('competitors.analyzingSteps', 'Checking keywords, technologies, competitors and ads...')}
            </p>
          </div>
        </div>
      )}

      {siteData && !isLoading && (
        <>
          {/* Analysis Confidence Badge */}
          {siteData.analysisConfidence && (
            <div className={`border rounded-lg p-3 flex items-start gap-3 ${
              siteData.analysisConfidence === 'verified'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                : siteData.analysisConfidence === 'partial'
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                  : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
            }`}>
              {siteData.analysisConfidence === 'verified' ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  siteData.analysisConfidence === 'partial'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`} />
              )}
              <div className={`text-sm ${
                siteData.analysisConfidence === 'verified'
                  ? 'text-green-800 dark:text-green-300'
                  : siteData.analysisConfidence === 'partial'
                    ? 'text-blue-800 dark:text-blue-300'
                    : 'text-amber-800 dark:text-amber-300'
              }`}>
                <strong>
                  {siteData.analysisConfidence === 'verified'
                    ? t('competitors.verifiedData', 'Verified Data')
                    : siteData.analysisConfidence === 'partial'
                      ? t('competitors.partialData', 'Partial Data')
                      : t('competitors.estimatedData', 'Estimated Data')}
                </strong>
                {': '}
                {siteData.analysisConfidence === 'verified'
                  ? t('competitors.verifiedDescription', '{{count}} palavras-chave confirmadas direto no Google.', { count: siteData.verifiedKeywordsCount || 0 })
                  : siteData.analysisConfidence === 'partial'
                    ? t('competitors.partialDescription', 'Some data verified, some estimated.')
                    : t('competitors.estimatedDataDescription', 'Traffic and keywords are estimates based on web analysis.')}
              </div>
            </div>
          )}

          {/* Domain Header */}
          <Card>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <Globe2 className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {siteData.domain}
                    </h2>
                    <a
                      href={`https://${siteData.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      {t('common.visitSite', 'Visit site')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                <Button onClick={handleAddCompetitor}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('competitors.addToMyCompetitors', 'Add to Competitors')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Main Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
            <Card padding={false}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.organicTraffic', 'Traffic')}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatNumber(siteData.organicTraffic)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Target className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">DA</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {siteData.domainAuthority ?? '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Link2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.backlinks', 'Backlinks')}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatNumber(siteData.backlinks)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <BarChart3 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('keywords.keywords', 'Keywords')}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatNumber(siteData.organicKeywords)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 p-1.5 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                    <Code2 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.technologies', 'Techs')}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {siteData.technologyStack?.totalCount || '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card padding={false}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 p-1.5 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                    <Megaphone className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {t('competitors.ads', 'Ads')}
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {siteData.adsIntelligence?.hasGoogleAds ? (
                        <span className="text-green-600">{t('common.yes', 'Yes')}</span>
                      ) : (
                        <span className="text-gray-400">{t('common.no', 'No')}</span>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          {/* Celular: abas viram botões que quebram linha (sem rolagem lateral) */}
          <div className="sm:border-b border-gray-200 dark:border-gray-700">
            <nav className="flex flex-wrap sm:flex-nowrap gap-1.5 sm:gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2.5 text-xs sm:text-sm font-medium rounded-full sm:rounded-none border sm:border-0 sm:border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 sm:bg-transparent sm:dark:bg-transparent text-primary-600 dark:text-primary-400'
                      : 'border-gray-200 dark:border-gray-700 sm:border-transparent sm:dark:border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-3 sm:space-y-6">
              {/* Traffic History Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('competitors.trafficHistory', 'Traffic History')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={siteData.trafficHistory || []}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                      <XAxis dataKey="date" className="text-gray-500" tick={{ fontSize: 12 }} />
                      <YAxis className="text-gray-500" tick={{ fontSize: 12 }} tickFormatter={formatNumber} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#fff', borderColor: '#e5e7eb' }}
                        formatter={(value: number) => formatNumber(value)}
                      />
                      <Area type="monotone" dataKey="organic" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name={t('competitors.organicTraffic', 'Organic Traffic')} />
                      <Area type="monotone" dataKey="paid" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name={t('competitors.paidTraffic', 'Paid Traffic')} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Pages and Traffic by Country */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('competitors.topPages', 'Top Pages')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(siteData.topPages || []).slice(0, 5).map((page, index) => (
                        <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center text-xs font-bold text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                              {index + 1}
                            </span>
                            <a
                              href={page.url.startsWith('http') ? page.url : `https://${page.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-600 dark:text-blue-400 text-sm hover:underline truncate"
                              title={page.url}
                            >
                              {page.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}...
                            </a>
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white text-sm">
                            {formatNumber(page.traffic)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('competitors.trafficByCountry', 'Traffic by Country')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <div className="w-1/2">
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={siteData.trafficByCountry || []}
                              cx="50%"
                              cy="50%"
                              innerRadius={35}
                              outerRadius={70}
                              paddingAngle={2}
                              dataKey="share"
                            >
                              {(siteData.trafficByCountry || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COUNTRY_COLORS[index % COUNTRY_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => `${value}%`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-1/2 space-y-2">
                        {(siteData.trafficByCountry || []).slice(0, 5).map((country, index) => (
                          <div key={country.country} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COUNTRY_COLORS[index % COUNTRY_COLORS.length] }} />
                              <span className="text-gray-700 dark:text-gray-300">{country.country}</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white">{country.share}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'keywords' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('competitors.topOrganicKeywords', 'Top Organic Keywords')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto scrollbar-hide">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('keywords.keyword')}</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('keywords.position')}</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('keywords.searchVolume')}</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('keywords.traffic', 'Traffic')}</th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.source', 'Source')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(siteData.topKeywords || []).map((keyword, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-gray-900 dark:text-white">{keyword.keyword}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3">
                            <Badge variant={keyword.position <= 3 ? 'success' : keyword.position <= 10 ? 'warning' : 'gray'}>
                              #{keyword.position}
                            </Badge>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400">{formatNumber(keyword.volume)}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400">{formatNumber(keyword.traffic)}</td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3">
                            {keyword.source === 'serper_verified' ? (
                              <Badge variant="success">{t('common.verified', 'Verified')}</Badge>
                            ) : (
                              <Badge variant="gray">{t('common.estimated', 'Estimated')}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'technologies' && (
            <div className="space-y-3 sm:space-y-6">
              {siteData.technologyStack ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(siteData.technologyStack)
                      .filter(([key, value]) => key !== 'totalCount' && Array.isArray(value) && value.length > 0)
                      .map(([category, techs]) => (
                        <Card key={category}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <span className="p-1.5 rounded-lg" style={{ backgroundColor: `${TECH_CATEGORY_COLORS[category]}20` }}>
                                <span style={{ color: TECH_CATEGORY_COLORS[category] }}>
                                  {getTechCategoryIcon(category)}
                                </span>
                              </span>
                              {getTechCategoryLabel(category)}
                              <Badge variant="gray" className="ml-auto">{(techs as string[]).length}</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex flex-wrap gap-2">
                              {(techs as string[]).map((tech, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-1 text-xs font-medium rounded-full"
                                  style={{
                                    backgroundColor: `${TECH_CATEGORY_COLORS[category]}15`,
                                    color: TECH_CATEGORY_COLORS[category]
                                  }}
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                  {Object.entries(siteData.technologyStack).filter(([key, value]) => key !== 'totalCount' && Array.isArray(value) && value.length > 0).length === 0 && (
                    <Card>
                      <CardContent className="text-center py-12">
                        <Code2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-gray-400">
                          {t('competitors.noTechnologiesDetected', 'No technologies detected')}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <Code2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('competitors.techDetectionNotAvailable', 'Technology detection not available')}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'competitors' && (
            <div className="space-y-3 sm:space-y-6">
              {siteData.competitionMetrics && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('competitors.competitionLevel', 'Competition Level')}</p>
                      <Badge
                        variant={siteData.competitionMetrics.competitionLevel === 'high' ? 'danger' : siteData.competitionMetrics.competitionLevel === 'medium' ? 'warning' : 'success'}
                        className="mt-1"
                      >
                        {siteData.competitionMetrics.competitionLevel.toUpperCase()}
                      </Badge>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('competitors.topCompetitor', 'Top Competitor')}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                        {siteData.competitionMetrics.topCompetitor || '-'}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('competitors.keywordOverlap', 'Keyword Overlap')}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                        {siteData.competitionMetrics.topCompetitorOverlap || 0}%
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {siteData.competitors && siteData.competitors.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('competitors.discoveredCompetitors', 'Discovered Competitors')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto scrollbar-hide">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('competitors.domain', 'Domain')}</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('competitors.keywordOverlap', 'Overlap')}</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('keywords.avgPosition', 'Avg. Position')}</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('competitors.level', 'Level')}</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('competitors.commonKeywords', 'Common Keywords')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {siteData.competitors.map((comp, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <a
                                  href={`https://${comp.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  {comp.domain}
                                </a>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary-500 rounded-full"
                                      style={{ width: `${Math.min(comp.overlapPercentage, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-gray-600 dark:text-gray-400">{comp.overlapPercentage}%</span>
                                </div>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-600 dark:text-gray-400">
                                {comp.avgPosition ? `#${comp.avgPosition}` : '-'}
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <Badge variant={comp.competitionLevel === 'high' ? 'danger' : comp.competitionLevel === 'medium' ? 'warning' : 'success'}>
                                  {comp.competitionLevel}
                                </Badge>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-3">
                                <div className="flex flex-wrap gap-1">
                                  {comp.commonKeywords.slice(0, 3).map((kw, i) => (
                                    <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                                      {kw.slice(0, 20)}...
                                    </span>
                                  ))}
                                  {comp.commonKeywords.length > 3 && (
                                    <span className="px-2 py-0.5 text-xs text-gray-500">+{comp.commonKeywords.length - 3}</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <Swords className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('competitors.noCompetitorsFound', 'No competitors discovered. Need more keywords to analyze.')}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'ads' && (
            <div className="space-y-3 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Google Ads</p>
                        <p className="text-lg font-bold">
                          {siteData.adsIntelligence?.hasGoogleAds ? (
                            <span className="text-green-600">{t('common.active', 'Active')}</span>
                          ) : (
                            <span className="text-gray-400">{t('common.inactive', 'Inactive')}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Meta Ads</p>
                        <p className="text-lg font-bold">
                          {siteData.adsIntelligence?.hasMetaAds === true ? (
                            <span className="text-green-600">{t('common.active', 'Active')}</span>
                          ) : siteData.adsIntelligence?.hasMetaAds === false ? (
                            <span className="text-gray-400">{t('common.inactive', 'Inactive')}</span>
                          ) : (
                            <span className="text-gray-400">{t('common.unknown', 'Unknown')}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <Megaphone className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('competitors.adsCount', 'Ads Found')}</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {siteData.adsIntelligence?.googleAdsCount || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent className="py-8 text-center">
                  <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {t('competitors.checkAdLibraries', 'Check Ad Libraries')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
                    {t('competitors.adLibrariesDescription', 'For complete ad intelligence, visit Google and Meta ad transparency centers.')}
                  </p>
                  <div className="flex justify-center gap-3">
                    <a
                      href={`https://adstransparency.google.com/advertiser/${siteData.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Google Ads Transparency
                    </a>
                    <a
                      href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=BR&q=${encodeURIComponent(siteData.domain)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Meta Ad Library
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!siteData && !isLoading && history.length === 0 && (
        <Card>
          <CardContent className="text-center py-16">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('competitors.exploreAnyDomain', 'Explore any domain')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('competitors.siteExplorerEmptyState', 'Enter a domain above to discover its organic traffic, ranked keywords, technologies, competitors and more.')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SiteExplorerPage;
